/* eslint-disable no-shadow */
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  IconButton,
  Divider,
  Drawer,
  CircularProgress,
  useMediaQuery,
  Grid,
  Button,
} from "@mui/material";
import {
  GavelOutlined,
  HelpOutline,
  PolicyOutlined,
} from "@mui/icons-material";
import { useLogoutMutation } from "../../../../../graphql/generated/graphql";

import { setAuth } from "../../../../hooks/auth";
import { clearToken, clearRefreshToken } from "../../../../helper/auth";
import { ItemSidebar } from "../../../../config/interfaces";
import NavItem from "./nav-item";
import UserInfoContent from "./user-info";
import { LogoSVG } from "../../../../assets/svg";
import { theme } from "../../..";
import { useApolloClient } from "@apollo/client";

const heightValue = 64;
const widthValue = 280;

const items: ItemSidebar[] = [
  {
    href: "/help",
    icon: <HelpOutline fontSize="small" />,
    title: "Help & Support",
  },
  {
    href: "/privacy-policy",
    icon: <PolicyOutlined fontSize="small" />,
    title: "Privacy policy",
  },
  {
    href: "/terms-and-conditions",
    icon: <GavelOutlined fontSize="small" />,
    title: "Terms and Conditions",
  },
];

const DashboardSidebar = (props: any) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { open, onClose } = props;
  const [loader, setLoader] = useState(false);
  const [logoutMutation] = useLogoutMutation();
  const lgUp = useMediaQuery(theme.breakpoints.up("md"));
  const client = useApolloClient();

  const handleLogout = async () => {
    setLoader(true);
    try {
      await logoutMutation();
      client.cache.reset();
      client.clearStore();
      clearToken();
      clearRefreshToken();
      setAuth(false);
      navigate("/");
      localStorage.clear();
    } catch (e) {
      setLoader(false);
    }
  };

  const content = (
    <>
      <Grid
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100vh",
        }}
      >
        <section>
          <Box
            sx={{
              background: "#000102",
              height: heightValue,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <IconButton
              href="/"
              sx={{
                width: "80%",
              }}
            >
              <LogoSVG />
            </IconButton>
          </Box>
        </section>
        <section>
          <Grid
            sx={{
              height: `calc( 100vh - ${heightValue}px)`,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              p: 2,
            }}
          >
            <Box
              sx={{
                height: "calc( 100vh - 250px)",
                overflowY: "auto",
              }}
            >
              <UserInfoContent />
            </Box>
            <Box>
              <Box sx={{ flexGrow: 1 }}>
                <Divider
                  sx={{
                    width: "100%",
                    borderColor: "divider",
                    mb: 1,
                  }}
                />
                {items.map((item) => (
                  <Grid key={item.title}>
                    <NavItem
                      icon={item.icon}
                      href={item.href}
                      title={item.title}
                      active={location.pathname === item.href}
                    />
                  </Grid>
                ))}
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  onClick={handleLogout}
                >
                  {loader ? <CircularProgress color="primary" /> : "Log Out"}
                </Button>
              </Box>
            </Box>
          </Grid>
        </section>
      </Grid>
    </>
  );

  if (lgUp) {
    return (
      <Drawer
        anchor="left"
        open
        PaperProps={{
          sx: {
            backgroundColor: "#323643",
            color: "#FFFFFF",
            width: widthValue,
            maxWidth: "80%",
          },
        }}
        variant="permanent"
      >
        {content}
      </Drawer>
    );
  }

  return (
    <Drawer
      anchor="left"
      onClose={onClose}
      open={open}
      PaperProps={{
        sx: {
          backgroundColor: "#323643",
          color: "#FFFFFF",
          width: widthValue,
          maxWidth: "80%",
        },
      }}
      sx={{ zIndex: (theme) => theme.zIndex.appBar + 100 }}
      variant="temporary"
    >
      {content}
    </Drawer>
  );
};

export default DashboardSidebar;
